<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router'
import { computed, defineAsyncComponent } from 'vue'
import OperatorBreadcrumb from '@/components/operator/OperatorBreadcrumb.vue'
import {
  GlbStatus,
  OperatorStepTreeDataQuery,
  OperatorStepTreeDataQueryVariables,
  StepChangeKind,
} from '@/generated/graphql'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import OperatorStepTree from '@/components/operator/OperatorStepTree.vue'
import OperatorCurrentProductionBatch from '@/components/operator/OperatorCurrentProductionBatch.vue'
import useTree, { TreeItemNode } from '@/composables/useTree'
import OperatorModuleStepper from '@/components/operator/OperatorModuleStepper.vue'

const BopStepModelViewer = defineAsyncComponent(
  () => import('@/components/bop/BopStepModelViewer.vue'),
)

type OperatorModuleViewQuery = {
  product: {
    bop: { uploads: Array<{ glbStatus: GlbStatus }> }
    module: OperatorStepTreeDataQuery['product']['module']
  }
}

export type OperatorModuleViewTreeNode = TreeItemNode<OperatorModuleViewNode>
export type OperatorModuleViewModule = OperatorStepTreeDataQuery['product']['module']
export type OperatorModuleViewNode = OperatorModuleViewModule['nodes'][0] & { removed?: boolean }

const route = useRoute()
const router = useRouter()

const bopId = computed(() => route.params.bopId as string)
const moduleId = computed(() => route.params.moduleId as string)
const stepId = computed(() => route.params.stepId as string | undefined)

const fetchQuery = useQuery<OperatorModuleViewQuery, OperatorStepTreeDataQueryVariables>(
  gql`
    query OperatorStepTreeData($bopId: ID!, $moduleId: ID!) {
      product {
        bop(id: $bopId) {
          id
          uploads {
            glbStatus
          }
        }
        module(id: $moduleId) {
          id
          abbreviation
          nodes(bopId: $bopId) {
            id
            parentId
            changeKind
            movedFrom
            isLibraryReference
            step {
              id
              name
              icons {
                id
                name
                imageUpload {
                  id
                  imageUrl(options: { width: 64, height: 64 })
                }
              }
              stepType {
                id
                name
              }
            }
          }
          removedSteps(bopId: $bopId) {
            parentStepId
            afterStepId
            step {
              id
              name
              icons {
                id
                name
                imageUpload {
                  id
                  imageUrl(options: { width: 64, height: 64 })
                }
              }
              stepType {
                id
                name
              }
            }
          }
        }
      }
    }
  `,
  () => ({ bopId: bopId.value, moduleId: moduleId.value }),
)

const abbreviation = computed(() => fetchQuery.result.value?.product.module.abbreviation || '')
const stepNodes = computed<OperatorModuleViewNode[]>(
  () => fetchQuery.result.value?.product.module.nodes || [],
)
const removedSteps = computed(() => fetchQuery.result.value?.product.module.removedSteps || [])

// Removed steps join the tree as ghost nodes at the place they used to occupy
const nodesWithGhosts = computed<OperatorModuleViewNode[]>(() => {
  const nodes = [...stepNodes.value]

  const behindGhostsAt = (index: number, parentId?: string) => {
    let position = index + 1
    while (
      position < nodes.length &&
      nodes[position].removed &&
      nodes[position].parentId == parentId
    ) {
      position++
    }
    return position
  }

  for (const removed of removedSteps.value) {
    const parentNode = removed.parentStepId
      ? nodes.find((n) => n.step.id == removed.parentStepId)
      : undefined
    const ghost: OperatorModuleViewNode = {
      id: `removed-${removed.step.id}`,
      parentId: parentNode?.id,
      changeKind: StepChangeKind.Removed,
      isLibraryReference: false,
      step: removed.step,
      removed: true,
    }
    const afterIndex = removed.afterStepId
      ? nodes.findIndex((n) => n.step.id == removed.afterStepId && n.parentId == ghost.parentId)
      : -1

    if (afterIndex >= 0) {
      nodes.splice(behindGhostsAt(afterIndex, ghost.parentId), 0, ghost)
    } else if (parentNode) {
      nodes.splice(behindGhostsAt(nodes.indexOf(parentNode), ghost.parentId), 0, ghost)
    } else {
      nodes.splice(behindGhostsAt(-1, ghost.parentId), 0, ghost)
    }
  }
  return nodes
})
const hasGlbFile = computed(() => {
  const uploads = fetchQuery.result.value?.product.bop.uploads ?? []
  return uploads.some(
    (u) =>
      u.glbStatus === GlbStatus.Done ||
      u.glbStatus === GlbStatus.Queued ||
      u.glbStatus === GlbStatus.Converting,
  )
})
const { treeRootNodes } = useTree()
const treeNodes = computed(() => treeRootNodes(nodesWithGhosts, false))

function selectStep(id: string | undefined) {
  router.push({ params: { stepId: id } })
}
</script>

<template>
  <div class="d-flex flex-column fill-height">
    <operator-breadcrumb />
    <operator-current-production-batch
      :step-id="stepId"
      :module-id="moduleId"
      class="my-2 flex-0-0"
    />

    <operator-module-stepper
      :bop-id="bopId"
      :module-id="moduleId"
      :step-id="stepId"
      :nodes="treeNodes"
      @select-step-id="selectStep"
    />
  </div>

  <operator-step-tree
    :abbreviation="abbreviation"
    :nodes="treeNodes"
    :loading="fetchQuery.loading.value"
    :selected-step-id="stepId"
    @update:selected-step-id="selectStep"
  >
    <template v-if="hasGlbFile" #bottom>
      <bop-step-model-viewer
        :bop-id="bopId"
        :module-id="moduleId"
        :step-id="stepId"
        show-controls-help
      />
    </template>
  </operator-step-tree>
</template>
